import { useAdapterCatalogue } from '../features/gps/hooks/use-adapter-catalogue';
import { useGpsIntegrations } from '../features/gps/hooks/use-gps-integrations';
import { useSmsIntegrations } from '../features/sms/hooks/use-sms-integrations';
import { useWhatsappIntegrations } from '../features/whatsapp/hooks/use-whatsapp-integrations';
import type { WhatsappIntegrationsState } from '../features/whatsapp/hooks/use-whatsapp-integrations';
import type { SmsIntegrationsState } from '../features/sms/hooks/use-sms-integrations';
import { installableProviders } from '../policies/messaging-provider.policy';

/** Everything the Integrations page shows: each kind's installations and what the catalogue still offers. */
export function useIntegrationsOverview() {
  const sms = useSmsIntegrations();
  const whatsapp = useWhatsappIntegrations();
  const gps = useGpsIntegrations();
  const adapters = useAdapterCatalogue();
  const smsLoaded = sms.state.status === 'loaded' ? sms.state : undefined;
  const whatsappLoaded = whatsapp.state.status === 'loaded' ? whatsapp.state : undefined;

  return {
    sms,
    whatsapp,
    gps,
    adapters,
    ...smsOverview(smsLoaded),
    ...whatsappOverview(whatsappLoaded),
    gpsAdapters: adapters.status === 'loaded' ? adapters.adapters : [],
    isCatalogueLoading: !smsLoaded || !whatsappLoaded || adapters.status === 'loading',
  };
}

function smsOverview(loaded: Extract<SmsIntegrationsState, { status: 'loaded' }> | undefined) {
  return {
    smsProviders: loaded?.providers,
    smsSendingName: loaded?.integrations.find((integration) => integration.isSending)?.displayName,
    /** SMS providers not installed yet: the catalogue's SMS entries. */
    installableSms: loaded ? installableProviders(loaded.providers, loaded.integrations) : [],
  };
}

function whatsappOverview(loaded: Extract<WhatsappIntegrationsState, { status: 'loaded' }> | undefined) {
  return {
    whatsappProviders: loaded?.providers,
    whatsappSendingName: loaded?.integrations.find((integration) => integration.isSending)?.displayName,
    /** WhatsApp providers not installed yet: the catalogue's WhatsApp entries. */
    installableWhatsapp: loaded ? installableProviders(loaded.providers, loaded.integrations) : [],
  };
}

export type IntegrationsOverview = ReturnType<typeof useIntegrationsOverview>;
